import { useCallback, useEffect, useMemo, useState } from 'react';
import { WizardAccordion } from '@hotjar/feedback/components/common/Wizard/WizardAccordion';
import type {
  SurveySectionProps,
  SurveySectionKey,
} from '@hotjar/feedback/domain/common/Wizard/Wizard';
import { useWizardSteps } from './useWizardSteps';
import { useSectionsContent } from './useSectionsContent';

export interface SurveyWizardAccordionProps extends SurveySectionProps {
  activeSection: SurveySectionKey;
  hiddenSections: Partial<Record<SurveySectionKey, boolean>>;
  openedSections: Partial<Record<SurveySectionKey, boolean>>;
  isNameValid: boolean;
  isLocationValid: boolean;
  isQuestionsValid: boolean;
  isAppearanceValid: boolean;
  isInvitationValid: boolean;
  isTargetingValid: boolean;
  isBehaviorValid: boolean;
  isForwardingValid: boolean;
  mode: 'edit' | 'create';
  isDisabled: boolean;
  onChangeActiveSection: (sectionName: SurveySectionKey) => void;
  onToggleSection: (sectionName: SurveySectionKey) => void;
}

export const SurveyWizardAccordion = ({
  activeSection,
  hiddenSections,
  openedSections,
  isNameValid,
  isLocationValid,
  isQuestionsValid,
  isAppearanceValid,
  isInvitationValid,
  isTargetingValid,
  isBehaviorValid,
  isForwardingValid,
  isDisabled,
  onChangeActiveSection,
  onToggleSection,
  ...sectionProps
}: SurveyWizardAccordionProps): JSX.Element | null => {
  const { mode } = sectionProps;
  const stepsProps = useMemo(
    () => ({ activeSection, hiddenSections, openedSections, onToggleSection }),
    [activeSection, hiddenSections, openedSections, onToggleSection],
  );
  const validationProps = useMemo(() => {
    return {
      name: isNameValid,
      location: isLocationValid,
      questions: isQuestionsValid,
      appearance: isAppearanceValid,
      invitation: isInvitationValid,
      targeting: isTargetingValid,
      behavior: isBehaviorValid,
      forwarding: isForwardingValid,
      review: true,
    };
  }, [
    isNameValid,
    isLocationValid,
    isQuestionsValid,
    isAppearanceValid,
    isInvitationValid,
    isTargetingValid,
    isBehaviorValid,
    isForwardingValid,
  ]);
  const steps = useWizardSteps(stepsProps, validationProps);

  const { sections, summarySection } = useSectionsContent(steps, activeSection, sectionProps);

  useEffect(() => {
    onChangeActiveSection(activeSection);
  }, [activeSection, onChangeActiveSection]);

  const summaryIndex = steps.length;
  const [isSummaryOpen, setIsSummaryOpen] = useState(activeSection === 'review' || mode === 'edit');

  useEffect(() => {
    if (isSummaryOpen) {
      setTimeout(() => {
        const summaryHeader = document.getElementById('section-review');
        summaryHeader?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isSummaryOpen]);

  const activeSections = useMemo(() => {
    if (isDisabled) return [];
    const activeStepIndex = steps.findIndex(({ key }) => key === activeSection);
    return [
      activeStepIndex !== -1 ? activeStepIndex : undefined,
      isSummaryOpen || activeSection === 'review' ? summaryIndex : undefined,
    ].filter((index) => index !== undefined) as number[];
  }, [isSummaryOpen, steps, activeSection, summaryIndex, isDisabled]);

  const toggleSection = useCallback(
    (toggledSectionIndex: number) => {
      if (toggledSectionIndex === summaryIndex) {
        if (activeSection !== 'review') {
          setIsSummaryOpen((isOpen) => !isOpen);
        } else {
          setIsSummaryOpen(false);
          onToggleSection('review');
        }
      } else {
        const sectionKey = steps[toggledSectionIndex].key;
        onToggleSection(sectionKey);
      }
    },
    [summaryIndex, steps, onToggleSection, activeSection],
  );

  return (
    <WizardAccordion
      isDisabled={isDisabled}
      activeSteps={activeSections}
      steps={sections}
      onChange={toggleSection}
      showSummary={hiddenSections.review !== true}
      summary={summarySection}
    />
  );
};
